import { useState } from 'react';
import Modal from '../../Modal';
import { Button, DateInput } from '@safetyhub/ui';
import {
  createScheduleChangeRequest,
  ScheduleChangeRequestType,
  CreateScheduleChangeRequestInput,
} from '../../../lib/inspections';
import { useToastStore } from '../../../stores/toast';
import { parseDateLocalToUTC } from '@safetyhub/shared';
import { todayLocalDateInput } from '../../../lib/localDate';

interface ScheduleChangeRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  inspectionId: string;
  currentScheduledAt?: string;
  onSuccess?: () => void;
}

export function ScheduleChangeRequestModal({
  isOpen,
  onClose,
  inspectionId,
  currentScheduledAt,
  onSuccess,
}: ScheduleChangeRequestModalProps) {
  const { showToast } = useToastStore();
  const [requestType, setRequestType] = useState<ScheduleChangeRequestType>('RESCHEDULE');
  const [requestedDate, setRequestedDate] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      showToast('사유를 입력해주세요.', 'error');
      return;
    }
    if (requestType === 'RESCHEDULE' && !requestedDate) {
      showToast('희망 날짜를 선택해주세요.', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const data: CreateScheduleChangeRequestInput = {
        requestType,
        reason: reason.trim(),
      };
      if (requestType === 'RESCHEDULE' && requestedDate) {
        // 날짜 문자열을 UTC ISO로 변환 (로컬 00:00:00 기준)
        data.requestedDate = parseDateLocalToUTC(requestedDate).toISOString();
      }

      const res = await createScheduleChangeRequest(inspectionId, data);
      if (res.success) {
        showToast('변경 요청이 제출되었습니다. Owner 승인 후 적용됩니다.', 'success');
        handleClose();
        onSuccess?.();
      } else {
        showToast('변경 요청 제출에 실패했습니다.', 'error');
      }
    } catch {
      showToast('변경 요청 제출에 실패했습니다.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setRequestType('RESCHEDULE');
    setRequestedDate('');
    setReason('');
    onClose();
  };

  const currentDateLabel = currentScheduledAt
    ? new Date(currentScheduledAt).toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '-';

  // 오늘 이후 날짜만 선택 가능 — **브라우저 로컬** 오늘이다. 종전 `toISOString().split('T')[0]`은 UTC
  // 날짜라 KST 00:00~09:00에는 `min`이 어제로 열려 지난 날짜를 고를 수 있었다(리뷰 1103 F7)
  const today = todayLocalDateInput();

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="점검 스케줄 변경 요청"
      description="Owner에게 변경 요청을 보냅니다. 승인 후 적용됩니다."
      size="md"
      preventClose={isSubmitting}
      footer={
        <>
          <Button type="button" onClick={handleClose} disabled={isSubmitting}>
            취소
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? '제출 중...' : '요청 제출'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* 현재 예약일 */}
        <div className="bg-surface-soft rounded-card px-4 py-3">
          <p className="text-xs text-ink-500">현재 예약일</p>
          <p className="text-sm font-medium text-ink-900 mt-0.5">{currentDateLabel}</p>
        </div>

        {/* 요청 유형 */}
        <div>
          <label className="block text-sm font-medium text-ink-700 mb-2">요청 유형</label>
          <div className="flex gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="requestType"
                value="RESCHEDULE"
                checked={requestType === 'RESCHEDULE'}
                onChange={() => setRequestType('RESCHEDULE')}
                className="text-brand-600"
              />
              <span className="text-sm text-ink-700">날짜 변경</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="requestType"
                value="CANCEL"
                checked={requestType === 'CANCEL'}
                onChange={() => setRequestType('CANCEL')}
                className="text-brand-600"
              />
              <span className="text-sm text-ink-700">점검 취소</span>
            </label>
          </div>
        </div>

        {/* 희망 날짜 (날짜 변경 시만) */}
        {requestType === 'RESCHEDULE' && (
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1">
              희망 날짜 <span className="text-red-500">*</span>
            </label>
            <DateInput
              label="희망 날짜"
              value={requestedDate}
              min={today}
              onChange={setRequestedDate}
              className="w-full"
            />
          </div>
        )}

        {/* 사유 */}
        <div>
          <label className="block text-sm font-medium text-ink-700 mb-1">
            사유 <span className="text-red-500">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={requestType === 'CANCEL' ? '취소 사유를 입력해주세요.' : '변경이 필요한 사유를 입력해주세요.'}
            rows={3}
            maxLength={500}
            className="w-full border border-ink-300 rounded-control px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          />
          <p className="text-xs text-ink-400 text-right mt-1">{reason.length}/500</p>
        </div>

        {requestType === 'CANCEL' && (
          <div className="bg-amber-50 border border-amber-200 rounded-card px-4 py-3">
            <p className="text-xs text-amber-700">
              취소 요청이 승인되면 이 점검은 삭제됩니다.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}
